import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Layers, Plus, Trash2, Pencil, FileText, ListChecks, Package, AlertTriangle } from "lucide-react";
import TemplateEditorDialog from "@/components/TemplateEditorDialog";

/**
 * Templates (Työpohjat) page — /app/tyopohjat
 *
 * Yrityksen omat työpohjat: uudelleenkäytettävä paketti työvaiheita,
 * havaintolistoja, vakiotarvikkeita ja muita kerättäviä tietoja.
 *
 * Käyttö:
 * - Rakenna työpohjat kerran → uuden raportin luonti Wizardissa vie 30–60s
 *   kun valitaan valmis työpohja.
 * - EI sisällä: yksikköjä, laitteita, kuvia, kohteita. Nämä ovat aina
 *   raportti-spesifejä.
 */
export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorTarget, setEditorTarget] = useState(null); // template object OR {} for new

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/content-templates");
      setTemplates(data.templates || []);
    } catch {
      toast.error("Työpohjien lataus epäonnistui");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const remove = async (tpl) => {
    if (!window.confirm(`Poista työpohja "${tpl.name}"? Tätä ei voi peruuttaa.`)) return;
    try {
      await api.delete(`/content-templates/${tpl.id}`);
      toast.success("Työpohja poistettu");
      refresh();
    } catch {
      toast.error("Poisto epäonnistui");
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8" data-testid="templates-page">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl md:text-3xl font-semibold text-slate-900 flex items-center gap-2.5">
            <Layers className="h-6 w-6 md:h-7 md:w-7 text-emerald-600" />
            Työpohjat
          </h1>
          <p className="text-sm md:text-base text-slate-500 mt-1.5 leading-relaxed max-w-2xl">
            Rakenna yrityksesi yleisimmät työt kerran. Uuden raportin luonnissa valitset
            työpohjan ja työvaiheet, havaintolistat, tarvikkeet ja muut kerättävät tiedot
            esitäytetään automaattisesti.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditorTarget({})}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm whitespace-nowrap flex-shrink-0 shadow-sm"
          data-testid="templates-create"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">Uusi työpohja</span>
          <span className="sm:hidden">Uusi</span>
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500 text-sm">
          Ladataan…
        </div>
      ) : templates.length === 0 ? (
        <EmptyState onCreate={() => setEditorTarget({})} />
      ) : (
        <div className="space-y-3">
          {templates.map((tpl) => (
            <TemplateRow
              key={tpl.id}
              template={tpl}
              onEdit={() => setEditorTarget(tpl)}
              onDelete={() => remove(tpl)}
            />
          ))}
        </div>
      )}

      {/* Editor dialog */}
      {editorTarget && (
        <TemplateEditorDialog
          template={editorTarget}
          onClose={() => setEditorTarget(null)}
          onSaved={() => {
            setEditorTarget(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/40 p-8 md:p-12 text-center" data-testid="templates-empty">
      <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 mb-4">
        <Layers className="h-7 w-7" />
      </div>
      <h2 className="font-display text-lg md:text-xl font-semibold text-slate-900">
        Ei työpohjia vielä
      </h2>
      <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto leading-relaxed">
        Rakenna ensimmäinen työpohja — esim. <em>&quot;IV-vuosihuolto — kerrostalo&quot;</em>. Sen jälkeen uuden raportin luonti vie alle minuutin.
      </p>
      <button
        type="button"
        onClick={onCreate}
        className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm shadow-sm"
        data-testid="templates-empty-create"
      >
        <Plus className="h-4 w-4" />
        Luo ensimmäinen työpohja
      </button>
    </div>
  );
}

function TemplateRow({ template, onEdit, onDelete }) {
  const workCount = useMemo(
    () => (template.base_checklist || []).filter((s) => !(template.base_checklist_findings || []).includes(s)).length,
    [template.base_checklist, template.base_checklist_findings],
  );
  const findingCount = (template.base_checklist_findings || []).length;
  const materialCount = (template.materials || []).length;
  const customCount = (template.custom_fields || []).length;

  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 hover:border-slate-300 transition-colors"
      data-testid={`template-row-${template.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={onEdit}
          className="flex-1 min-w-0 text-left"
          data-testid={`template-open-${template.id}`}
        >
          <div className="font-semibold text-slate-900 text-base md:text-lg">{template.name}</div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs md:text-sm text-slate-500">
            <StatBadge icon={ListChecks} count={workCount} label="työvaihetta" />
            <StatBadge icon={AlertTriangle} count={findingCount} label="havaintoa" />
            <StatBadge icon={Package} count={materialCount} label="tarviketta" />
            <StatBadge icon={FileText} count={customCount} label="muuta kenttää" />
          </div>
        </button>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={onEdit}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
            data-testid={`template-edit-${template.id}`}
            aria-label="Muokkaa"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
            data-testid={`template-delete-${template.id}`}
            aria-label="Poista"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function StatBadge({ icon: Icon, count, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5 text-slate-400" />
      <span className="tabular-nums">{count}</span> {label}
    </span>
  );
}
